import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useQuery } from 'react-query';
import {
  Box,
  Text,
  ChevronDownIcon,
  ChevronUpIcon,
  Link,
  Divider,
  Amount,
  Spinner,
} from '@razorpay/blade/components';

import { WarningAlert } from 'components/ui/Alert';
import { useModal } from 'components/ui/Modals';
import useHasPermission from 'utils/useHasPermission';
import permissions from 'constants/permissions';
import loggedInUserSelectors from 'reducers/loggedInUser/selectors';
import api from 'api';
import { FETCH_RX_BALANCE } from 'components/RunPayroll/utils/queries';
import { useRunPayrollDataContext } from 'components/RunPayroll/contexts/RunPayrollDataContext';
import { canFinalizePayroll } from 'components/RunPayroll/utils/utils';
import useNewJoinerArrearEnabled from 'components/RunPayroll/utils/useNewJoinerArrearEnabled';
import PayrollAmountBreakdown from 'components/RunPayroll/Popups/PayrollAmountBreakdown';
import { StyledAmountCardWrapper, StyledRazorpayChargesCard } from './styles';
import { ClickableAmount } from './ClickableAmount';
import { ChargesSummary } from './ChargesSummary';
import { AmountRow } from './AmountRow';
import { RazorpayChargesBreakdownV2 } from './RazorpayChargesBreakdownV2';
import { getTooltipContent } from './utils';

export const AmountDisplayV2 = () => {
  const { openModal } = useModal();
  const [showRazorpayChargesModal, setShowRazorpayChargesModal] = useState(false);
  const handleCloseRazorpayChargesModal = () => {
    setShowRazorpayChargesModal(false);
  };
  const handleOpenRazorpayChargesModal = () => {
    setShowRazorpayChargesModal(true);
  };
  const isAuthorizedToViewBalance = useHasPermission([permissions.COMPANY_VIEW_BALANCE]);

  const payrollData = useRunPayrollDataContext();
  const orgId = useSelector(loggedInUserSelectors.organizationID);
  const isRXIntegrated = useSelector(loggedInUserSelectors.isOrgRXIntegrated);
  const { isNewJoinerArrearFeatureEnabled } = useNewJoinerArrearEnabled();

  const [isExpanded, setIsExpanded] = useState(false);

  const {
    isLoading: isBalanceLoading,
    isRefetching: isBalanceRefetching,
    data: rxBalanceData,
  } = useQuery(
    FETCH_RX_BALANCE,
    () => {
      if (orgId) {
        return api.runPayroll.getRXBalance(orgId);
      }
    },
    {
      enabled: !!isRXIntegrated,
    },
  );

  if (!payrollData) {
    return null;
  }

  const rxBalance = rxBalanceData?.balance;

  const {
    totalGross,
    opfinCharges,
    orgBalance,
    requiredAmount,
    isPayrollFinalized,
    flexiPartnerName,
    isPayrollBeingFinalized,
    isBulkAdditionDeductionInProgress,
    isChangeRequestPendingOnFinalize,
    numPendingEditRequest,
  } = payrollData;

  const { pricingDetails } = requiredAmount?.['opfin-charges'] ?? {};

  const showPayrollBreakdown = () => {
    if (requiredAmount) {
      openModal(
        <PayrollAmountBreakdown
          requiredAmount={requiredAmount}
          flexiPartnerName={flexiPartnerName}
        />,
      );
    }
  };

  let finalizeMessage = 'Finalize your payroll to view the required amount.';
  if (isBulkAdditionDeductionInProgress) {
    finalizeMessage =
      'Finalize your payroll to view the required amount. This is currently disabled as there is an ongoing bulk operation on your payroll data.';
  }

  if (isPayrollBeingFinalized || isChangeRequestPendingOnFinalize || numPendingEditRequest > 0) {
    return null;
  }

  // The banner asks the admin to finalize, so it follows the same visibility rule
  // as the Finalize button itself (canFinalizePayroll). When the button is not on
  // offer and there is no amount to show (fully executed month, compute failure,
  // execution in flight), stay quiet - InfoDisplay owns any messaging there.
  if (
    canFinalizePayroll({
      data: payrollData,
      isCountDrivenVisibility: !isNewJoinerArrearFeatureEnabled,
    })
  ) {
    return <WarningAlert className="mb-10">{finalizeMessage}</WarningAlert>;
  }

  if (!isPayrollFinalized || requiredAmount == null) {
    return null;
  }

  const totalAmount = totalGross + opfinCharges;

  const isCardCollapsible = opfinCharges > 0;

  const isRazorpayChargesClickable = opfinCharges > 0;

  return (
    <>
      <StyledAmountCardWrapper>
        <StyledRazorpayChargesCard>
          <Box
            display="flex"
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center">
            <Text size="medium" weight="semibold" color="surface.text.gray.muted">
              Total Amount
            </Text>
            {isCardCollapsible && (
              <div
                onClick={() => setIsExpanded(!isExpanded)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    setIsExpanded(!isExpanded);
                  }
                }}
                role="button"
                tabIndex={0}
                style={{ cursor: 'pointer' }}
                aria-label={isExpanded ? 'Collapse details' : 'Expand details'}
                aria-expanded={isExpanded}>
                {isExpanded ? (
                  <ChevronUpIcon size="large" color="surface.icon.gray.muted" />
                ) : (
                  <ChevronDownIcon size="large" color="surface.icon.gray.muted" />
                )}
              </div>
            )}
          </Box>
          <Box display="flex" flexDirection="column" gap="spacing.2">
            <ClickableAmount
              value={totalAmount}
              onAmountClick={!isCardCollapsible ? showPayrollBreakdown : undefined}
            />
            {isCardCollapsible && <ChargesSummary />}
          </Box>
        </StyledRazorpayChargesCard>

        {isCardCollapsible && isExpanded && (
          <>
            <AmountRow
              label="Payroll Amount"
              value={totalGross}
              onAmountClick={showPayrollBreakdown}
            />
            <Divider variant="muted" />
            <AmountRow
              label="Razorpay Charges"
              value={opfinCharges}
              tooltipContent={getTooltipContent(pricingDetails)}
              onAmountClick={
                isRazorpayChargesClickable ? handleOpenRazorpayChargesModal : undefined
              }
            />
          </>
        )}
      </StyledAmountCardWrapper>

      {isAuthorizedToViewBalance && (
        <StyledAmountCardWrapper>
          <Box
            display="flex"
            flexDirection="column"
            gap="spacing.2"
            paddingY="spacing.4"
            paddingX="spacing.5"
            backgroundColor="surface.background.gray.moderate"
            borderRadius="medium"
            marginTop="spacing.2"
            marginX="spacing.2">
            <Box
              display="flex"
              flexDirection="row"
              justifyContent="space-between"
              alignItems="center">
              <Text size="small" weight="semibold" color="surface.text.gray.normal">
                Current Balance
              </Text>
            </Box>

            <Box>
              {isBalanceLoading || isBalanceRefetching ? (
                <Box display="flex" justifyContent="center" alignItems="center">
                  <Spinner accessibilityLabel="loading balance" />
                </Box>
              ) : (
                <>
                  <Amount
                    value={isRXIntegrated ? rxBalance ?? 0 : orgBalance ?? 0}
                    suffix="decimals"
                    size="large"
                    weight="semibold"
                  />
                  <Box>
                    <Link
                      href="https://razorpay.com/docs/payroll/faqs/#1-when-will-payroll-update-my-account-balance"
                      target="_blank"
                      size="xsmall">
                      Balance not updated?
                    </Link>
                  </Box>
                </>
              )}
            </Box>
          </Box>
        </StyledAmountCardWrapper>
      )}

      <RazorpayChargesBreakdownV2
        isOpen={showRazorpayChargesModal}
        onClose={handleCloseRazorpayChargesModal}
      />
    </>
  );
};
